import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Skeleton,
  SkeletonItem,
  Tab,
  TabList,
  makeStyles,
} from '@fluentui/react-components';
import { ArrowLeft, RefreshCw } from 'lucide-react';

import { ApiError } from '../api/client';
import RelativeTime from '../components/RelativeTime';
import { useGroupDetailQuery } from '../hooks/useGroupDetail';
import { HeaderFact, PageHeader } from '../components/abPrimitives';
import { ACCENT, NUMERIC, STROKE } from '../theme/tokens';

// FD-288 (FD-ADR-031 §1) — group detail as TABS in the content column.
//
// The LAST of the three full-height sub-rails ADR-031 supersedes, after FD-281
// (migrations) and FD-284 (tenants). Same treatment for the same reason: the
// tabs navigate the same child routes the sub-rail did, so tab state is the URL
// and the existing route tests keep meaning what they meant. The
// `min-h-[100dvh]` wrapper that existed only to sit flush against a rail goes
// with the rail — AppShell owns the padded content column now.

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'migrations', label: 'Migrations' },
  { key: 'settings', label: 'Settings' },
] as const;

const useStyles = makeStyles({
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '6px',
    // FD-466 — the accent TEXT rung, from the token module rather than a
    // literal. Every one of these back links carried a hardcoded hex through
    // two accent retirements (sky-700, then Rubix orange) and the drift gate
    // caught them both times; taking the value from `ACCENT` is what stops a
    // third.
    color: ACCENT.pressed,
    fontSize: '13px',
    textDecorationLine: 'none',
    ':hover': {
      textDecorationLine: 'underline',
    },
  },
});

export default function GroupLayout() {
  const styles = useStyles();
  const { groupId } = useParams<{ groupId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // The layout owns the canonical observer; the three tab children call the
  // same hook, so react-query dedupes to ONE fetch. That is what keeps the
  // detail page's device fan-out to once per visit rather than once per tab.
  const query = useGroupDetailQuery(groupId);

  if (query.isError && query.error instanceof ApiError && query.error.status === 404) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-10">
        <div className="max-w-xl">
          <MessageBar intent="warning">
            <MessageBarBody>
              <MessageBarTitle>Group not found</MessageBarTitle>
              No migration group with ID <span className="font-mono text-xs">{groupId}</span>{' '}
              exists. It may have been deleted.
            </MessageBarBody>
            <MessageBarActions>
              <Link to="/groups" className={styles.backLink}>
                Back to groups
              </Link>
            </MessageBarActions>
          </MessageBar>
        </div>
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-10">
        <DetailSkeleton />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="max-w-[1400px] mx-auto px-8 py-10">
        <div className="max-w-xl">
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Could not load group</MessageBarTitle>
              {errorMessage(query.error)}
            </MessageBarBody>
            <MessageBarActions>
              <Button
                appearance="subtle"
                size="small"
                icon={<RefreshCw size={14} strokeWidth={1.5} />}
                onClick={() => query.refetch()}
              >
                Retry
              </Button>
            </MessageBarActions>
          </MessageBar>
        </div>
      </div>
    );
  }

  const { migrationGroup: group, rollup } = query.data;

  // Read from the URL, not from state — a deep link must open on the right tab.
  const activeTab =
    TABS.find((t) => location.pathname.endsWith(`/${t.key}`))?.key ?? 'overview';

  return (
    <div className="flex flex-col gap-6">
      <Link to="/groups" className={styles.backLink}>
        <ArrowLeft size={14} strokeWidth={1.5} />
        All groups
      </Link>

      {/* FD-467 — the AirBridge PageHeader. The facts row replaces the
          middot-separated meta line; nothing is dropped, and the labels are
          what make "assigned" readable as a count rather than as progress. */}
      <PageHeader
        title={group.name}
        subtitle={group.description || undefined}
        facts={
          <>
            <HeaderFact label="Campaigns">
              <span style={NUMERIC}>{rollup.migrationCount.toLocaleString()}</span>
            </HeaderFact>
            {/* "assigned", not a progress figure — DeviceCount moves only on
                add-devices / remove-devices, never on wave completion. */}
            <HeaderFact label="Devices assigned">
              <span style={NUMERIC}>{rollup.deviceCount.toLocaleString()}</span>
            </HeaderFact>
            <HeaderFact label="Updated">
              <RelativeTime iso={group.updatedAt} /> by {group.updatedBy}
            </HeaderFact>
          </>
        }
      />

      <div style={{ borderBottom: `1px solid ${STROKE.base}` }}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, data) => navigate(`/groups/${groupId}/${data.value}`)}
        >
          {TABS.map((t) => (
            <Tab key={t.key} value={t.key}>
              {t.label}
            </Tab>
          ))}
        </TabList>
      </div>

      <Outlet />
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton>
        <SkeletonItem size={16} style={{ width: '120px' }} />
      </Skeleton>
      <div style={{ display: 'flex', flexDirection: 'column', rowGap: '12px' }}>
        <Skeleton>
          <SkeletonItem size={32} style={{ width: '40%' }} />
        </Skeleton>
        <Skeleton>
          <SkeletonItem size={16} style={{ width: '70%' }} />
        </Skeleton>
      </div>
      <Skeleton>
        <SkeletonItem size={128} style={{ width: '100%' }} />
      </Skeleton>
    </div>
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}
